import React from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  Edit, Trash2, Archive, PenTool, GitBranch, FilePlus,
  CheckCircle, XCircle, Send, Share2, Download, Printer,
  MoreHorizontal,
} from "lucide-react";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger,
} from "../ui/dropdown-menu";
import { statusLabels, statusColors, GDPR_PROTECTED, IN_WORKFLOW } from "../../lib/contractUtils";

export function ContractHeader({
  contract, workflow, user, id,
  canReview, canApprove,
  esignConfigured,
  onSignatureDialog, onWorkflowDialog, onAddendumDialog, onDeleteDialog,
  onArchive, onSign, onCancelWorkflow, onResubmit, onShareDraft, onDownloadPdf,
  onPrintSign,
  openActionDialog,
}) {
  const isDeleteBlocked = GDPR_PROTECTED.includes(contract.status) || IN_WORKFLOW.includes(contract.status);
  const sigStatus = contract.signature_status;

  return (
    <div className="flex items-center gap-2 flex-wrap" data-testid="contract-header-actions">
      {/* ═══ PRIMARY ACTIONS — status-specific, always visible ═══ */}

      {/* Draft → Submit for Review */}
      {(contract.status === "draft" && !workflow) && (
        <Button onClick={onWorkflowDialog} className="gap-2 rounded-sm" data-testid="submit-review-btn">
          <GitBranch size={16} /> Submit for Review
        </Button>
      )}

      {/* Reviewer actions */}
      {canReview && (
        <>
          <Button onClick={() => openActionDialog("review-approve")} className="gap-2 rounded-sm bg-emerald-600 hover:bg-emerald-700" data-testid="review-approve-btn">
            <CheckCircle size={16} /> Approve Review
          </Button>
          <Button onClick={() => openActionDialog("send-back")} variant="outline" className="gap-2 rounded-sm text-orange-600 border-orange-200 hover:bg-orange-50" data-testid="send-back-btn">
            <Send size={16} /> Send Back
          </Button>
        </>
      )}

      {/* Approver actions */}
      {canApprove && (
        <>
          <Button onClick={() => openActionDialog("approve")} className="gap-2 rounded-sm bg-emerald-600 hover:bg-emerald-700" data-testid="approve-btn">
            <CheckCircle size={16} /> Approve
          </Button>
          <Button onClick={() => openActionDialog("reject")} variant="outline" className="gap-2 rounded-sm text-red-600 border-red-200 hover:bg-red-50" data-testid="reject-btn">
            <XCircle size={16} /> Reject
          </Button>
        </>
      )}

      {/* Approved → Signing options */}
      {contract.status === "approved" && !sigStatus && (
        <>
          <Button onClick={onPrintSign} className="gap-2 rounded-sm bg-cyan-600 hover:bg-cyan-700" data-testid="print-sign-btn">
            <Printer size={16} /> Print & Sign
          </Button>
          {esignConfigured && (
            <Button onClick={onSignatureDialog} variant="outline" className="gap-2 rounded-sm text-[#4C00FF] border-[#4C00FF]/30 hover:bg-[#4C00FF]/5" data-testid="request-signature-btn">
              <PenTool size={16} /> Send via DocuSign
            </Button>
          )}
        </>
      )}

      {/* Print & Sign upload reminder */}
      {contract.print_sign_status === "pdf_generated" && sigStatus !== "fully_executed" && (
        <Button onClick={onPrintSign} className="gap-2 rounded-sm bg-cyan-600 hover:bg-cyan-700" data-testid="print-sign-upload-btn">
          <Printer size={16} /> Upload Signed Copy
        </Button>
      )}

      {/* DocuSign internal sign */}
      {esignConfigured && sigStatus === "awaiting_internal" && (
        <Button onClick={onSign} className="gap-2 rounded-sm bg-[#4C00FF] hover:bg-[#3D00CC]" data-testid="sign-contract-btn">
          <PenTool size={16} /> Sign via DocuSign
        </Button>
      )}

      {/* Recall / Resubmit */}
      {workflow?.status === "pending" && workflow?.created_by === user?.id && workflow?.stage !== "revision" && (
        <Button onClick={onCancelWorkflow} variant="outline" className="gap-2 rounded-sm" data-testid="cancel-workflow-btn">
          <XCircle size={16} /> Recall
        </Button>
      )}
      {workflow?.stage === "revision" && workflow?.created_by === user?.id && (
        <Button onClick={onResubmit} variant="outline" className="gap-2 rounded-sm text-blue-600 border-blue-200 hover:bg-blue-50" data-testid="resubmit-review-btn">
          <Send size={16} /> Resubmit for Review
        </Button>
      )}

      {/* Status badges */}
      {sigStatus === "awaiting_counter_signature" && (
        <Badge className="bg-amber-100 text-amber-800 rounded-sm px-3 py-1.5 text-xs" data-testid="awaiting-counter-sig-badge">
          Awaiting Supplier Counter-Signature
        </Badge>
      )}
      {sigStatus === "fully_executed" && (
        <Badge className="bg-emerald-100 text-emerald-800 rounded-sm px-3 py-1.5 text-xs" data-testid="fully-executed-badge">
          <CheckCircle size={14} className="mr-1" /> Fully Executed
        </Badge>
      )}

      {/* ═══ MORE ACTIONS — dropdown for utility actions ═══ */}
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="outline" size="icon" className="rounded-sm h-9 w-9" data-testid="more-actions-btn">
            <MoreHorizontal size={16} />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-52">
          {/* Share Draft */}
          {(contract.status === "supplier_review" || (contract.status === "approved" && !sigStatus)) && (
            <DropdownMenuItem onClick={onShareDraft} className="gap-2 cursor-pointer" data-testid="menu-share-draft">
              <Share2 size={14} /> Share Draft with Supplier
            </DropdownMenuItem>
          )}

          {/* Download PDF */}
          <DropdownMenuItem onClick={onDownloadPdf} className="gap-2 cursor-pointer" data-testid="menu-download-pdf">
            <Download size={14} /> Download PDF
          </DropdownMenuItem>

          <DropdownMenuSeparator />

          {/* Edit */}
          <DropdownMenuItem asChild className="gap-2 cursor-pointer" data-testid="menu-edit">
            <Link to={`/contracts/${id}/edit`}>
              <Edit size={14} /> Edit Contract
            </Link>
          </DropdownMenuItem>

          {/* Addendum */}
          <DropdownMenuItem onClick={onAddendumDialog} className="gap-2 cursor-pointer" data-testid="menu-addendum">
            <FilePlus size={14} /> Add Addendum
          </DropdownMenuItem>

          <DropdownMenuSeparator />

          {/* Archive */}
          <DropdownMenuItem onClick={onArchive} className="gap-2 cursor-pointer" data-testid="menu-archive">
            <Archive size={14} /> Archive
          </DropdownMenuItem>

          {/* Delete */}
          <DropdownMenuItem
            onClick={() => {
              if (isDeleteBlocked) {
                toast.error(
                  GDPR_PROTECTED.includes(contract.status)
                    ? "This contract is protected under GDPR data-retention policy and cannot be deleted."
                    : "Cannot delete a contract in an active approval workflow."
                );
                return;
              }
              onDeleteDialog();
            }}
            className={`gap-2 cursor-pointer ${isDeleteBlocked ? "text-slate-400" : "text-red-600"}`}
            data-testid="menu-delete"
          >
            <Trash2 size={14} /> {isDeleteBlocked ? "Delete (Protected)" : "Delete"}
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}
